import { FolderX, Plus, UserPlus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { allowedTabs, resolveTab } from '@/lib/auth/permissions'
import { useProject } from './api'
import { AssignmentTab } from './components/detail/AssignmentTab'
import { CalendarTab } from './components/detail/CalendarTab'
import { FilesTab } from './components/detail/FilesTab'
import { KanbanTab } from './components/detail/KanbanTab'
import { ListTab } from './components/detail/ListTab'
import { NotesTab } from './components/detail/NotesTab'
import { OverviewTab } from './components/detail/OverviewTab'
import { ReportsTab } from './components/detail/ReportsTab'
import { SettingsTab } from './components/detail/SettingsTab'
import { SprintsTab } from './components/detail/SprintsTab'
import { TaskFilterBar } from './components/detail/TaskFilterBar'
import { InstallationsTab } from './components/install/InstallationsTab'
import { MemberFormModal } from './components/MemberFormModal'
import { MemberStack, ProjectIcon, ProjectStatusBadge } from './components/ProjectBits'
import { TaskFormModal, type TaskDefaults } from './components/task/TaskFormModal'
import { DETAIL_TAB_PERMS, DETAIL_TABS, type DetailTab } from './detailTabs'
import { PROJECT_TEAM_PERM, PROJECT_WORK_PERM } from './permissions'
import { EMPTY_FILTERS, type TaskFilters } from './taskFilters'
import type { Project } from './types'
import { useStaffLookup } from './ui'

const FILTERED_TABS: DetailTab[] = ['kanban', 'list', 'calendar']

export function ProjectDetailPage() {
  const { t } = useTranslation()
  const { projectId = '', tab: tabParam } = useParams()
  const project = useProject(projectId)

  if (tabParam && !DETAIL_TABS.includes(tabParam as DetailTab)) return <Navigate to={`/projects/${projectId}`} replace />
  if (project.isError) {
    return isApiError(project.error) && project.error.status === 404 ? (
      <EmptyState icon={FolderX} title={t('projects.notFound')} />
    ) : (
      <ErrorState error={project.error} onRetry={() => void project.refetch()} />
    )
  }
  if (!project.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    )
  }
  return <ProjectDetail project={project.data} tab={(tabParam as DetailTab | undefined) ?? 'overview'} />
}

function ProjectDetail({ project, tab: requested }: { project: Project; tab: DetailTab }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const tabs = allowedTabs(DETAIL_TABS, DETAIL_TAB_PERMS, can)
  const tab = resolveTab(requested, tabs)
  const navigate = useNavigate()
  const { nameOf } = useStaffLookup()
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_FILTERS)
  const [taskForm, setTaskForm] = useState<{ open: boolean; defaults?: TaskDefaults }>({ open: false })
  const [memberOpen, setMemberOpen] = useState(false)

  const go = (next: DetailTab) => navigate(next === 'overview' ? `/projects/${project.id}` : `/projects/${project.id}/${next}`)
  const newTask = (defaults?: TaskDefaults) => setTaskForm({ open: true, defaults })
  const openBoard = (installationId: string) => {
    setFilters({ ...EMPTY_FILTERS, installationId })
    go('kanban')
  }

  const content: Record<DetailTab, ReactNode> = {
    overview: <OverviewTab project={project} onNavigate={go} />,
    tasks: <AssignmentTab project={project} onNewTask={newTask} />,
    sprints: <SprintsTab project={project} />,
    kanban: <KanbanTab project={project} filters={filters} onNewTask={newTask} />,
    list: <ListTab project={project} filters={filters} />,
    calendar: <CalendarTab project={project} filters={filters} />,
    installations: <InstallationsTab project={project} onOpenBoard={openBoard} />,
    files: <FilesTab project={project} />,
    notes: <NotesTab project={project} />,
    reports: <ReportsTab project={project} />,
    settings: <SettingsTab project={project} />,
  }

  return (
    <>
      <PageHeader
        backTo={`/projects/boards?p=${project.id}`}
        title={
          <span className="flex flex-wrap items-center gap-3">
            <ProjectIcon icon={project.icon} color={project.color} className="size-9 rounded-lg" />
            {project.name}
            <ProjectStatusBadge status={project.status} />
          </span>
        }
        description={project.description}
        actions={
          <>
            <span className="hidden sm:block">
              <MemberStack names={project.memberIds.map(nameOf)} max={4} />
            </span>
            {can(PROJECT_TEAM_PERM) ? (
              <Button variant="outline" onClick={() => setMemberOpen(true)}>
                <UserPlus />
                {t('projects.detail.addMember')}
              </Button>
            ) : null}
            {can(PROJECT_WORK_PERM) ? (
              <Button onClick={() => newTask()}>
                <Plus />
                {t('projects.tasks.add')}
              </Button>
            ) : null}
          </>
        }
      />

      <Tabs value={tab} onValueChange={(v) => go(v as DetailTab)} className="mb-4">
        <TabsList>
          {tabs.map((k) => (
            <TabsTrigger key={k} value={k}>
              {t(`projects.detail.tabs.${k}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {FILTERED_TABS.includes(tab) ? <TaskFilterBar project={project} value={filters} onChange={setFilters} view={tab} onViewChange={go} /> : null}

      {content[tab]}

      <TaskFormModal open={taskForm.open} project={project} task={null} defaults={taskForm.defaults} onOpenChange={(open) => setTaskForm((s) => ({ ...s, open }))} />
      <MemberFormModal open={memberOpen} project={project} member={null} onOpenChange={setMemberOpen} />
    </>
  )
}
